import {
  capabilitySummary,
  hasBlockingFinding,
  inspectCapabilities,
  type CapabilityEnvironment,
  type CapabilityFinding,
} from "../services/capability";

function element<K extends keyof HTMLElementTagNameMap>(
  name: K,
  text?: string,
): HTMLElementTagNameMap[K] {
  const created = document.createElement(name);
  if (text !== undefined) created.textContent = text;
  return created;
}

/** Probes the live browser. Every probe that throws reports unknown, not ready. */
export function readEnvironment(): CapabilityEnvironment {
  let localStorageWorks: boolean | null;
  try {
    const probe = "atlas:capability-probe";
    window.localStorage.setItem(probe, "1");
    localStorageWorks = window.localStorage.getItem(probe) === "1";
    window.localStorage.removeItem(probe);
  } catch {
    localStorageWorks = false;
  }

  const synthesis = "speechSynthesis" in window ? window.speechSynthesis : null;
  let localVoiceCount: number | null = null;
  if (synthesis !== null) {
    try {
      const voices = synthesis.getVoices();
      // An empty list on first paint means not yet resolved, not zero.
      localVoiceCount =
        voices.length === 0
          ? null
          : voices.filter((voice) => voice.localService).length;
    } catch {
      localVoiceCount = null;
    }
  }

  return {
    localStorageWorks,
    speechSynthesisPresent: synthesis !== null,
    localVoiceCount,
    userAgent: window.navigator.userAgent,
  };
}

function findingRow(finding: CapabilityFinding): HTMLElement {
  const row = element("li");
  row.dataset.capability = finding.id;
  row.dataset.status = finding.status;
  row.append(element("strong", finding.name));
  row.append(element("p", finding.detail));
  if (finding.fix !== "") {
    const fix = element("p", finding.fix);
    fix.className = "capability-fix";
    row.append(fix);
  }
  return row;
}

/**
 * Renders the capability band. Doc 03 section 1.4 requires the Brave Shields
 * fix be displayed every time it occurs, so the band re-reads the environment
 * whenever the voice list changes rather than reporting once at first paint.
 */
export function mountCapabilityChecks(target: HTMLElement): () => void {
  const band = element("section");
  band.className = "capability-band";
  band.dataset.role = "capability-band";
  const heading = element("h2", "Before you start");
  const summary = element("p");
  summary.dataset.role = "capability-summary";
  const list = element("ul");
  list.dataset.role = "capability-findings";
  band.append(heading, summary, list);
  target.append(band);

  const render = (): void => {
    const findings = inspectCapabilities(readEnvironment());
    summary.textContent = capabilitySummary(findings);
    band.dataset.status = hasBlockingFinding(findings) ? "blocked" : "ready";
    list.replaceChildren(...findings.map(findingRow));
  };

  render();
  const synthesis = "speechSynthesis" in window ? window.speechSynthesis : null;
  synthesis?.addEventListener("voiceschanged", render);
  return () => synthesis?.removeEventListener("voiceschanged", render);
}
